import React from "react";
import { Team } from "../../models/teamModel";
import { DescriptionTerm } from "../catalyst/description-list";
import { UserView } from "../UserView";
import { Badge } from "../catalyst/badge";
import {
  classNames,
  getDisplayInviteStatus,
  getDisplayRole,
} from "../../utils/appUtils";
import { BadgeByRole } from "../BadgeByRole";
import { useConfirmationDialog } from "../ConfirmDialog";
import { useTeamsStore } from "../../store/teamsStore";

interface TeamInvitationsProps {
  team: Team;
}

const getBadgeColor = (status: string) => {
  switch (status) {
    case "accepted":
      return "green";
    case "rejected":
      return "red";
    default:
      return "blue";
  }
};

const InviteRow = ({ inv, index, cancelInviteClicked }: any) => {
  return (
    <>
      <div
        key={inv.emailSentAt}
        className={classNames(
          "flex flex-row ml-2 gap-4 py-1",
          index % 2 === 0 ? "bg-gray-100 dark:bg-gray-700" : "",
          "hidden sm:flex"
        )}
      >
        <UserView member={inv} />
        <BadgeByRole
          role={inv.inviteFor}
          displayRole={getDisplayRole(inv.inviteFor)}
        />
        <Badge color={getBadgeColor(inv.status)} className="text-xs">
          {getDisplayInviteStatus(inv.status)}
        </Badge>
        {inv.status === "pending" && (
          <button
            className="text-xs text-red-500"
            onClick={() => cancelInviteClicked(inv.id)}
          >
            Cancel Invite
          </button>
        )}
      </div>

      <div
        key={inv.emailSentAt}
        className={classNames(
          "flex flex-row ml-2 gap-4 py-1",
          index % 2 === 0 ? "bg-gray-100 dark:bg-gray-700" : "",
          "sm:hidden"
        )}
      >
        <div className="w-full">
          <UserView member={inv} />
          <div className="flex flex-wrap flex-row justify-between gap-4 w-full">
            <BadgeByRole
              role={inv.inviteFor}
              displayRole={getDisplayRole(inv.inviteFor)}
            />
            <Badge color={getBadgeColor(inv.status)} className="text-xs">
              {getDisplayInviteStatus(inv.status)}
            </Badge>
            {inv.status === "pending" && (
              <button
                className="text-xs text-red-500"
                onClick={() => cancelInviteClicked(inv.id)}
              >
                Cancel Invite
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export const TeamInvitations: React.FC<TeamInvitationsProps> = ({ team }) => {
  const { invitationDetails } = team;
  const { openDialog } = useConfirmationDialog();
  const { cancelInvite, getTeamDetails } = useTeamsStore();

  const cancelInviteClicked = (inviteId: string) => {
    openDialog({
      title: `Cancel Invite`,
      message: `Are you sure you want to cancel the invite?`,
      onConfirm: async () => {
        if (!team) return;
        await cancelInvite(team.id, inviteId);
        await getTeamDetails(team.id);
      },
    });
  };

  return (
    <div>
      <DescriptionTerm>
        Available Invitations: {invitationDetails.avalaibleInvites}
      </DescriptionTerm>
      <DescriptionTerm>
        Pending Invitations: {invitationDetails.pendingInvites}
      </DescriptionTerm>
      {invitationDetails.invitations &&
        invitationDetails.invitations
          .filter((i) => i.status === "pending")
          .map((inv, index) => (
            <InviteRow
              key={inv.emailSentAt}
              inv={inv}
              index={index}
              cancelInviteClicked={cancelInviteClicked}
            />
          ))}

      <DescriptionTerm>Sent Invitations</DescriptionTerm>
      {invitationDetails.invitations &&
        invitationDetails.invitations
          .filter((i) => i.status !== "pending")
          .map((inv, index) => (
            <InviteRow
              key={inv.emailSentAt}
              inv={inv}
              index={index}
              cancelInviteClicked={cancelInviteClicked}
            />
          ))}
    </div>
  );
};
